import { useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import SearchIcon from "@mui/icons-material/Search";
import useCategories from "./hooks/useCategories";
import { EmptyState } from "../../../../core/ui";
import { CategoriesService } from "../../../../core/http/services/CategoriesService";
import AuthContext from "@/contexts/AuthContext";

const emptyCategoryForm = { name: "" };

const Categories = () => {
  const { userLoggedData } = useContext(AuthContext);
  const { clubId: clubIdFromUrl } = useParams();
  const clubId = clubIdFromUrl || userLoggedData?.selectedClubId || null;

  const { categories, setCategories, fetchCategories } = useCategories();

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const [search, setSearch] = useState("");
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newCategoryForm, setNewCategoryForm] = useState(emptyCategoryForm);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const filteredCategories = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) return categories;
    return categories.filter((c) =>
      String(c.name || "").toLowerCase().includes(normalizedSearch),
    );
  }, [categories, search]);

  const handleDeleteCategory = async () => {
    if (!deleteTarget) return;
    if (!clubId) return;
    setDeleteLoading(true);
    const { status } = await CategoriesService.delete(clubId, deleteTarget.id);
    setDeleteLoading(false);
    setDeleteTarget(null);

    if (status === 204) {
      setCategories((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      setSnackbar({ open: true, message: "Categoria removida com sucesso.", severity: "success" });
    } else if (status === 409) {
      setSnackbar({ open: true, message: "Não é possível excluir: categoria possui jogadores ou grupos vinculados.", severity: "warning" });
    } else if (status === 404) {
      setSnackbar({ open: true, message: "Categoria não encontrada.", severity: "error" });
    } else {
      setSnackbar({ open: true, message: "Erro ao excluir a categoria.", severity: "error" });
    }
  };

  const handleAddCategory = async () => {
    if (!clubId) return;
    const { status } = await CategoriesService.create(clubId, {
      name: newCategoryForm.name.trim() || "Nova categoria",
    });

    if (status === 200 || status === 201) {
      await fetchCategories();
    }

    setNewCategoryForm(emptyCategoryForm);
    setAddModalOpen(false);
  };

  const handleFormChange = (field) => (event) => {
    setNewCategoryForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  return (
    <Stack spacing={2.5}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={1}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.05 }}>
            Categorias
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddModalOpen(true)}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          Adicionar categoria
        </Button>
      </Stack>

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          p: 1.2,
        }}
      >
        <TextField
          placeholder="Buscar categoria..."
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 360, mb: 1.2 }}
        />

        <Stack spacing={1}>
          {filteredCategories.length === 0 ? (
            <EmptyState
              icon={<CategoryOutlinedIcon sx={{ fontSize: 34 }} />}
              title="Nenhuma categoria cadastrada"
              description={search.trim() ? "Nenhuma categoria encontrada para este termo." : "Nenhuma categoria cadastrada ainda."}
              sx={{ py: 3 }}
            />
          ) : (
            filteredCategories.map((category) => (
              <Paper
                key={category.id}
                elevation={0}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1.5,
                  p: 1.1,
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography sx={{ fontWeight: 800 }}>
                    {category.name}
                  </Typography>

                  <Button
                    size="small"
                    color="error"
                    variant="text"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={() => setDeleteTarget(category)}
                    sx={{ textTransform: "none", fontWeight: 700 }}
                  >
                    Excluir
                  </Button>
                </Stack>
              </Paper>
            ))
          )}
        </Stack>
      </Paper>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => !deleteLoading && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Excluir categoria</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir a categoria{" "}
            <strong>{deleteTarget?.name}</strong>? Esta ação não pode ser
            desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteLoading}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteCategory}
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Adicionar categoria</DialogTitle>
        <DialogContent>
          <Stack spacing={1.3} sx={{ mt: 0.5 }}>
            <TextField
              fullWidth
              label="Nome da categoria"
              value={newCategoryForm.name}
              onChange={handleFormChange("name")}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddModalOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleAddCategory}>
            Adicionar
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default Categories;
